import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import logo from "../../../assets/logo.png";
import L from "../../../constants/baysportLabels";
import { TRIAL_DAYS } from "../../../constants/trial";
import {
  buildLandingPriceHighlights,
  PLAN_POSITIONING,
  LANDING_PLANS_FALLBACK,
} from "../../../constants/planMarketing";
import platformAPI from "../../../services/platformApi";
import { getDashboardPath } from "../../../utils/authRedirect";
import { buildClinicPortalUrl } from "../../../utils/portalHost";
import { hasAdminAccess } from "../../../utils/roles";
import { trackTryClick } from "../../../analytics/metrikaGoals";
import { yandexMetrikaParams } from "../../../analytics/yandexMetrika";
import {
  JOURNAL_LANDING_PAGE_TITLE,
  JOURNAL_LANDING_PAGE_DESCRIPTION,
  JOURNAL_LANDING_PAGE_KEYWORDS,
  JOURNAL_LANDING_PATH,
  LANDING_OG_IMAGE,
  LANDING_OG_IMAGE_ALT,
  buildJournalLandingJsonLd,
} from "../HomeLanding/landingSeo";
import { SITE_ORIGIN } from "../../../config/site";
import Preloader from "../../common/Preloader";
import Seo from "../../Seo";
import s from "./cleanAdLanding.module.css";

function formatRub(n) {
  return new Intl.NumberFormat("ru-RU").format(Number(n) || 0);
}

function ArtJournal() {
  return (
    <div className={`${s.art} ${s.artJournal}`} aria-hidden>
      <div className={s.artWindow}>
        <div className={s.artDots}>
          <i />
          <i />
          <i />
        </div>
        <div className={s.artRows}>
          <span className={s.artRowOk} />
          <span className={s.artRowOk} />
          <span className={s.artRowMiss} />
          <span className={s.artRowOk} />
        </div>
      </div>
      <div className={s.artToggle}>
        <b />
      </div>
    </div>
  );
}

function ArtSchedule() {
  return (
    <div className={`${s.art} ${s.artSchedule}`} aria-hidden>
      <div className={s.artCal}>
        {Array.from({ length: 9 }, (_, i) => (
          <i key={i} className={i === 4 ? s.artCalOn : undefined} />
        ))}
      </div>
      <div className={s.artBadge}>16:00</div>
    </div>
  );
}

function ArtParents() {
  return (
    <div className={`${s.art} ${s.artParents}`} aria-hidden>
      <div className={s.artCardMini}>
        <strong>8</strong>
        <span>занятий</span>
      </div>
      <div className={s.artCardMini}>
        <strong>✓</strong>
        <span>был</span>
      </div>
    </div>
  );
}

function ArtMembership() {
  return (
    <div className={`${s.art} ${s.artMembership}`} aria-hidden>
      <div className={s.artTicket}>
        <span>Абонемент</span>
        <strong>12 / 16</strong>
      </div>
      <div className={s.artCoin} />
    </div>
  );
}

const BENEFITS = [
  {
    title: "Отметки на занятии",
    text: "Открыли группу на телефоне и отметили, кто пришёл — прямо во время тренировки.",
    icon: "✓",
  },
  {
    title: "Абонемент обновляется сам",
    text: "После отметки занятие списывается с пакета. Остаток видно сразу, без подсчёта вечером.",
    icon: "↻",
  },
  {
    title: "Родители видят посещения",
    text: "Расписание и кто был — в кабинете секции. Не нужно отвечать на это в мессенджере.",
    icon: "◎",
  },
];

const CAPABILITIES = [
  {
    id: "journal",
    title: "Журнал посещаемости",
    bullets: [
      "Список группы на конкретное занятие",
      "Отметки с телефона в зале",
      "История посещений по ученику",
    ],
    Art: ArtJournal,
  },
  {
    id: "schedule",
    title: "Расписание",
    bullets: [
      "Повторяющиеся тренировки по группам",
      "Замены и отмены без отдельной таблицы",
      "Журнал открывается из нужного занятия",
    ],
    Art: ArtSchedule,
  },
  {
    id: "membership",
    title: "Абонементы",
    bullets: [
      "По числу занятий или по сроку",
      "Списание сразу после отметки",
      "Видно, у кого пакет заканчивается",
    ],
    Art: ArtMembership,
  },
  {
    id: "parents",
    title: "Кабинет для родителей",
    bullets: [
      "Расписание секции на телефоне",
      "Посещения и остаток абонемента",
      "Не нужно ставить приложение из магазина",
    ],
    Art: ArtParents,
  },
];

const STEPS = [
  {
    title: "Создайте секцию",
    text: `Название и группа — за несколько минут. ${TRIAL_DAYS} дней полного доступа, карту указывать не нужно.`,
  },
  {
    title: "Добавьте учеников",
    text: "Вручную или отправьте родителям ссылку. Сразу можно завести абонементы.",
  },
  {
    title: "Отметьте тренировку",
    text: "Откройте журнал с телефона. После этого родители увидят посещения сами.",
  },
];

const FAQ = [
  {
    q: "Нужен ли компьютер в зале?",
    a: "Нет. Журнал открывается в браузере телефона. Страницу можно сохранить на главный экран — как обычное приложение.",
  },
  {
    q: "Что происходит с абонементом после отметки?",
    a: "Занятие списывается автоматически. Вы и родитель сразу видите, сколько занятий осталось.",
  },
  {
    q: "Родители сами видят посещения?",
    a: "Да. В кабинете секции — расписание, кто был на занятии и остаток абонемента. Отдельное приложение из магазина не нужно.",
  },
  {
    q: "Нужно ли переносить всю базу из таблиц?",
    a: "Нет. Можно начать с тех, кто ходит сейчас: создать группу и отметить ближайшую тренировку. Остальных добавить позже.",
  },
  {
    q: "Сколько это стоит?",
    a: "Первые 7 дней бесплатно. Потом — от 790 ₽ в месяц за клуб. Журнал есть на всех тарифах.",
  },
];

export default function CleanJournalLanding() {
  const { isAuth, role, tenant } = useSelector((state) => state.auth);
  const hasClub = isAuth && tenant?.subdomain && hasAdminAccess(role);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    yandexMetrikaParams({ landing: "journal" });
  }, []);

  useEffect(() => {
    platformAPI
      .getPublicPlans()
      .then((r) => {
        const loaded = Array.isArray(r?.plans) ? r.plans : [];
        setPlans(loaded.length ? loaded : LANDING_PLANS_FALLBACK);
      })
      .catch(() => setPlans(LANDING_PLANS_FALLBACK))
      .finally(() => setPlansLoading(false));
  }, []);

  const featuredCode = useMemo(() => {
    const withBadge = plans.find((p) => p.badge);
    return withBadge?.code || plans[1]?.code || plans[0]?.code;
  }, [plans]);

  const onTry = () => trackTryClick({ landing: "journal" });

  return (
    <div className={s.page}>
      <Seo
        title={JOURNAL_LANDING_PAGE_TITLE}
        description={JOURNAL_LANDING_PAGE_DESCRIPTION}
        keywords={JOURNAL_LANDING_PAGE_KEYWORDS}
        canonicalUrl={`${SITE_ORIGIN}${JOURNAL_LANDING_PATH}`}
        ogImage={LANDING_OG_IMAGE}
        ogImageAlt={LANDING_OG_IMAGE_ALT}
        jsonLd={buildJournalLandingJsonLd(FAQ)}
      />

      {hasClub ? (
        <div className={s.sessionBar}>
          <span>
            Вы вошли в <strong>{tenant.name || tenant.subdomain}</strong>
          </span>
          <a
            href={buildClinicPortalUrl(tenant.subdomain, getDashboardPath(role))}
            className={`${s.btn} ${s.btnPrimary}`}
          >
            Открыть кабинет
          </a>
        </div>
      ) : null}

      <header className={s.nav}>
        <Link to="/" className={s.navBrand}>
          <img src={logo} alt="" className={s.navLogo} />
          <span>{L.productName}</span>
        </Link>
        <div className={s.navActions}>
          <a href="#capabilities" className={`${s.btn} ${s.btnGhost} ${s.navLink}`}>
            Возможности
          </a>
          <a href="#pricing" className={`${s.btn} ${s.btnGhost} ${s.navLink}`}>
            Тарифы
          </a>
          <Link to="/account/login" className={`${s.btn} ${s.btnGhost}`}>
            Войти
          </Link>
          <Link
            to="/register-clinic"
            className={`${s.btn} ${s.btnPrimary}`}
            onClick={onTry}
          >
            Попробовать
          </Link>
        </div>
      </header>

      <section className={s.hero}>
        <div className={s.heroInner}>
          <div className={s.heroCopy}>
            <p className={s.heroEyebrow}>
              Журнал посещаемости · {TRIAL_DAYS} дней бесплатно
            </p>
            <h1 className={s.heroTitle}>
              Электронный журнал
              <br />
              посещаемости секции
            </h1>
            <p className={s.heroLead}>
              Отметьте, кто пришёл на тренировку — с телефона, прямо в зале.
              Занятие спишется с абонемента, а родители увидят посещения в
              кабинете секции.
            </p>
            <div className={s.heroActions}>
              <Link
                to="/register-clinic"
                className={`${s.btn} ${s.btnPrimary} ${s.btnLg}`}
                onClick={onTry}
              >
                Попробовать {TRIAL_DAYS} дней бесплатно
              </Link>
              <a href="#capabilities" className={`${s.btn} ${s.btnSecondary} ${s.btnLg}`}>
                Смотреть возможности
              </a>
            </div>
            <p className={s.heroNote}>Карту указывать не нужно · от 790 ₽ после пробного периода</p>
          </div>
          <div className={s.heroVisual}>
            <ArtJournal />
          </div>
        </div>

        <div className={s.benefitRow}>
          {BENEFITS.map((item) => (
            <article key={item.title} className={s.benefitCard}>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
              <span className={s.benefitIcon}>{item.icon}</span>
            </article>
          ))}
        </div>
      </section>

      <section id="capabilities" className={s.section}>
        <h2 className={s.sectionTitle}>Что входит вместе с журналом</h2>
        <p className={s.sectionLead}>
          Журнал не отдельно. К нему уже подключены расписание, абонементы и
          кабинет, куда заходят родители.
        </p>
        <div className={s.capGrid}>
          {CAPABILITIES.map((cap) => (
            <article key={cap.id} className={s.capCard}>
              <div className={s.capArt}>
                <cap.Art />
              </div>
              <h3>{cap.title}</h3>
              <ul>
                {cap.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className={s.section}>
        <div className={s.promoGrid}>
          <article className={s.promoCard}>
            <div className={s.promoCopy}>
              <h3>Попробуйте на своей группе</h3>
              <p>
                {TRIAL_DAYS} дней полного доступа. Карту привязывать не нужно.
              </p>
              <Link
                to="/register-clinic"
                className={`${s.btn} ${s.btnPrimary}`}
                onClick={onTry}
              >
                Начать бесплатно
              </Link>
            </div>
            <ArtParents />
          </article>
          <article className={s.promoCard}>
            <div className={s.promoCopy}>
              <h3>Дальше — тариф от 790 ₽</h3>
              <p>
                Журнал есть на всех тарифах. Если секцию ведёте сами, обычно
                хватает стартового.
              </p>
              <a href="#pricing" className={`${s.btn} ${s.btnPrimary}`}>
                Смотреть тарифы
              </a>
            </div>
            <ArtMembership />
          </article>
        </div>
      </section>

      <section className={s.section}>
        <h2 className={`${s.sectionTitle} ${s.sectionTitleCenter}`}>
          Как начать
        </h2>
        <p className={`${s.sectionLead} ${s.sectionLeadCenter}`}>
          Три шага — и можно отмечать ближайшую тренировку.
        </p>
        <div className={s.stepsTrack}>
          {STEPS.map((step, i) => (
            <div key={step.title} className={s.stepCol}>
              <div className={s.stepNumWrap}>
                <span className={s.stepNum}>{i + 1}</span>
                {i < STEPS.length - 1 ? <span className={s.stepLine} /> : null}
              </div>
              <article className={s.stepCard}>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className={s.section}>
        <h2 className={s.sectionTitle}>Тарифы</h2>
        <p className={s.sectionLead}>
          Первые {TRIAL_DAYS} дней — полный доступ бесплатно. Карта не нужна.
          Потом выбираете тариф под размер секции.
        </p>
        {plansLoading ? (
          <Preloader variant="inline" label="Загрузка тарифов…" />
        ) : (
          <div className={s.pricing}>
            {plans.map((plan) => (
              <article
                key={plan.id}
                className={`${s.priceCard} ${
                  plan.code === featuredCode ? s.priceCardFeatured : ""
                }`}
              >
                {plan.badge ? <span className={s.badge}>{plan.badge}</span> : null}
                <h3>{plan.name}</h3>
                {PLAN_POSITIONING[plan.code] ? (
                  <p className={s.pricePos}>{PLAN_POSITIONING[plan.code]}</p>
                ) : null}
                <p className={s.priceAmount}>
                  {formatRub(plan.monthly_price_rub)} ₽
                  <small>/ мес</small>
                </p>
                <ul className={s.priceList}>
                  {buildLandingPriceHighlights(plan.code).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <Link
                  to="/register-clinic"
                  className={`${s.btn} ${
                    plan.code === featuredCode ? s.btnPrimary : s.btnSecondary
                  }`}
                  onClick={onTry}
                >
                  {TRIAL_DAYS} дней бесплатно
                </Link>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Частые вопросы</h2>
        <p className={s.sectionLead}>
          Коротко о журнале в зале, абонементах и том, что видят родители.
        </p>
        <div className={s.faqList}>
          {FAQ.map((item, index) => (
            <details key={item.q} className={s.faqItem} open={index === 0}>
              <summary>
                <span>{item.q}</span>
                <span className={s.faqChevron} aria-hidden />
              </summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className={s.ctaBand}>
        <h2>Отметьте ближайшую тренировку</h2>
        <p>
          {TRIAL_DAYS} дней бесплатно. Потом — от 790 ₽ в месяц. Карту указывать
          не нужно.
        </p>
        <Link
          to="/register-clinic"
          className={`${s.btn} ${s.btnPrimary} ${s.btnLg}`}
          onClick={onTry}
        >
          Попробовать бесплатно
        </Link>
      </section>

      <footer className={s.footer}>
        © {new Date().getFullYear()} {L.productName}. Журнал посещаемости для
        спортивных секций.
      </footer>

      {!hasClub ? (
        <div className={s.sticky}>
          <Link
            to="/register-clinic"
            className={`${s.btn} ${s.btnPrimary}`}
            onClick={onTry}
          >
            {TRIAL_DAYS} дней бесплатно
          </Link>
        </div>
      ) : null}
    </div>
  );
}
